import { rememberReturn, unwrap } from '@hive/api';
import { Modal, ProblemBox, TextField, useToast } from '@hive/ui';
import { useState } from 'react';
import { useLocation } from 'react-router';
import { useT } from '../i18n';
import { useSession } from '../lib/session';

/**
 * R01-17 B: a ação sensível pediu MFA recente (403 step-up-required). Confirma no provedor de identidade (acr_values /
 * prompt=login, volta para a mesma tela) ou, se ele não faz step-up, com o código do autenticador local; depois a
 * pessoa repete a ação.
 */
export function StepUpDialog() {
  const t = useT();
  const s = useSession();
  const toast = useToast();
  const location = useLocation();
  const [mode, setMode] = useState<'ask' | 'local'>('ask');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  if (!s.stepUp) return null;
  const close = () => {
    setMode('ask');
    setCode('');
    setError(null);
    s.clearStepUp();
  };
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await unwrap(s.api.POST('/api/v1/auth/step-up', {}));
      if (r.mode === 'provider' && r.url) {
        rememberReturn(location.pathname + location.search);
        window.location.assign(r.url);
        return;
      }
      setMode('local');
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      await unwrap(s.api.POST('/api/v1/auth/step-up/verify', { body: { code } }));
      // o MFA novo fica na sessão: o próximo token (renovado pelo cookie) já leva a hora dele
      await s.auth.renew?.();
      toast(t('idpt.su.done'), { kind: 'ok' });
      close();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      title={t('idpt.su.title')}
      size="sm"
      onClose={close}
      foot={
        <>
          {mode === 'ask' ? (
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void start()}>
              {t('idpt.su.confirm')}
            </button>
          ) : (
            <button type="button" className="btn btn-primary" disabled={busy || code.replace(/\s/g, '').length < 6} onClick={() => void verify()}>
              {t('idpt.su.verify')}
            </button>
          )}
          <button type="button" className="btn" onClick={close}>
            {t('ui.cancel')}
          </button>
        </>
      }
    >
      <p>{t('idpt.su.text', { minutes: Number(s.stepUp.raw?.maxAgeMinutes ?? 15) })}</p>
      {error ? <ProblemBox error={error} /> : null}
      {mode === 'local' && (
        <TextField label={t('idpt.su.code')} value={code} onChange={setCode} inputMode="numeric" autoComplete="one-time-code" maxLength={11} />
      )}
    </Modal>
  );
}
